/**
 * 🎬 CustomNode — React Flow 节点的核心渲染组件
 *
 * 每个画布节点（aiNode 类型）都通过此组件渲染。
 * 根据 node.data 中的状态分发到不同子视图：
 *   - TextNodeView  — 空节点 / 纯文本提示
 *   - ImageNodeView — 图片展示 + 裁剪 / 旋转 / 去背景
 *   - VideoNodeView — 视频播放 + 封面 / 时长
 *   - AudioNodeView — 音频播放 / 配音
 *
 * 节点行为回调（onGenerate、onUpdate、onReupload 等）通过
 * NodeCallbackContext 注入，避免 props drilling。
 */
import React, { useRef, useState, memo, useEffect, useCallback } from 'react';
import { 
  Handle, Position, useUpdateNodeInternals, useViewport,
  useStore
} from 'reactflow';
import { X, Sparkles, ChevronRight, Image as ImageIconLucide, AlertTriangle, RotateCcw, ExternalLink, Loader2 } from 'lucide-react';

import { styles, LoadingOverlay, ExtraCard, NebulaBackground } from './node-parts/NodeCommons';
import NodeConsole from './node-parts/NodeConsole';
import TextNodeView from './node-parts/TextNodeView';
import ImageNodeView from './node-parts/ImageNodeView';
import VideoNodeView from './node-parts/VideoNodeView';
import AudioNodeView from './node-parts/AudioNodeView';
import { useInputProtection } from './hooks/useInputProtection';
import { useNodeCallbacks } from './NodeCallbackContext';
import { useMenuOpen } from './MenuOpenContext';
import MjToolbar from './node-parts/MjToolbar';
import GptInpaintToolbar from './node-parts/GptInpaintToolbar';
import H3UpscaleToolbar from './node-parts/H3UpscaleToolbar';
import DescriptionEditModal from './components/DescriptionEditModal';
import { DescriptionIcon, descBadgeStyle, descBadgeHover } from './components/DescriptionBadge';

// --- 自定义图标组件 (SVG ICON) ---
import { IconFilm, MIN_NODE_WIDTH } from './editorUtils';
const IconMusic = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="4" style={{ display: 'block' }}>
    <path d="M24 44c11.046 0 20-8.954 20-20S35.046 4 24 4S4 12.954 4 24s8.954 20 20 20Z"/><path strokeLinecap="round" strokeLinejoin="round" d="M26 14v14"/><path strokeLinejoin="round" d="M14 28.666C14 26.64 15.934 25 18.32 25H26v4.334C26 31.36 24.066 33 21.68 33h-3.36C15.934 33 14 31.359 14 29.334z"/><path strokeLinecap="round" strokeLinejoin="round" d="m32 15l-6-1"/>
  </svg>
);
const IconPhoto = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <path d="M15 8h.01M4 15l4-4c.928-.893 2.072-.893 3 0l5 5"/><path d="m14 14l1-1c.928-.893 2.072-.893 3 0l2 2"/><path d="M3 12a9 9 0 0 0 9 9a9 9 0 0 0 9-9a9 9 0 0 0-9-9a9 9 0 0 0-9 9"/>
  </svg>
);

// --- 智能端口 ---
/**
 * SmartHandle 智能连接点组件
 * 
 * 核心功能：
 * 1. 🎨 动态样式：未连接（蓝色）→ 已连接（绿色）
 * 2. 💡 悬停提示：右侧 handle 显示"双击连接节点"提示
 * 3. 📍 精确定位：无硬偏移，ReactFlow 自动计算连接点位置
 * 
 * ⚠️ 定位与缩放说明：
 * - ReactFlow 默认将 handle 定位在节点边缘正中间（left: 0/right: 0, top: 50%）
 * - 不设置 transform，完全使用 React Flow 默认 CSS class 处理定位
 * - ❌ 不要添加 scale(1/zoom) 抗缩放！CSS scale 会改变元素边界框，
 *   导致 React Flow 缓存的 handle 位置与实际视觉位置不一致，连线起点/终点偏移
 * - handle 随画布自然缩放即可，视觉上与节点保持一致
 */
const SmartHandle = ({ type, position, id, isConnectable, isConnected, zoom, visible, style, ...props }) => {
  const isLeft = position === Position.Left;
  const handleRef = useRef(null);
  // 菜单打开期间（加下游节点这步未完成）全局抑制拾取圈：不管鼠标在哪都不出现。
  const menuOpen = useMenuOpen();
  // follow：鼠标在触发区内时圆圈跟随的本地坐标（相对 Handle 左上角，未缩放）；null = 不跟随
  const [follow, setFollow] = useState(null);
  // pressing：按住拖线期间隐藏圆圈（改由 React Flow 的实时连线接管）
  const [pressing, setPressing] = useState(false);

  // 鼠标在触发区/圆圈上移动 → 圆圈跟到鼠标（本地坐标 = 屏幕位移 / zoom，因节点被视口缩放）
  const onZoneMove = (e) => {
    if (pressing) return;
    const rect = handleRef.current?.getBoundingClientRect();
    if (!rect) return;
    const z = zoom || 1;
    let x = (e.clientX - rect.left) / z;
    const y = (e.clientY - rect.top) / z;
    // 🚧 限制圆圈不越过节点边线进入画框：圆圈半径 11px。
    //   右侧点：圆圈左缘(x-11) ≥ 0 → x ≥ 11；左侧点：圆圈右缘(x+11) ≤ 0 → x ≤ -11。
    if (isLeft) x = Math.min(x, -11); else x = Math.max(x, 11);
    setFollow({ x, y });
  };
  const onZoneLeave = () => setFollow(null);

  // 按下圆圈：开始拖线，隐藏跟随圆圈；松手后复位（下次移动再重新跟随）
  const onCirclePress = () => {
    setPressing(true);
    const clear = () => {
      setPressing(false); setFollow(null);
      window.removeEventListener('mouseup', clear);
      window.removeEventListener('touchend', clear);
    };
    window.addEventListener('mouseup', clear);
    window.addEventListener('touchend', clear);
  };

  // 1) Handle 本体：贴边线、透明、1px。连线端点按它的包围盒算 → 永远连在边线上。
  //   mouseleave 挂在它上面：离开它及所有后代（触发区+圆圈）才触发，跟随不闪。
  const handleStyle = {
    boxSizing: 'border-box',
    width: '1px', height: '10px', minWidth: '1px', minHeight: '10px',
    background: 'transparent', border: 'none',
    pointerEvents: 'none',
    [isLeft ? 'left' : 'right']: '0px',
    zIndex: 10,
    ...style
  };

  // 2) 触发区：贴边线外一小块，光标为加号。是 Handle 后代 → 进它就让节点进 hover 态。
  //   ⚠️ 尺寸要紧凑：拾取区太大时，相邻上下游节点的两个触发区会在中间重叠，
  //   分不清拾取的是哪个点；按住左键平移画布也容易在节点附近被它抢走。
  //   宽 28px：盖住静止圆圈靠内的大半（圆圈占边线外 14~36px），保留一点提前感应；
  //   同时把「两个相邻节点触发区重叠」的模糊带压到节点间距 56px 以内才出现。
  //   高 36px：圆圈 22px + 上下各 7px 余量，不会悬在节点上下边缘外还被抢走。
  //   ⚠️ 圆圈本体自带 pointerEvents:'all'，触发区再小也不影响直接点圆圈拖线。
  const zoneStyle = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    [isLeft ? 'right' : 'left']: '0px',
    width: '28px', height: '36px',
    cursor: 'crosshair',
    pointerEvents: 'all',
    zIndex: 1
  };

  // 连接点绿点：很小的实心圆点，压在节点边线中点上，始终可见（标记接口位置）。
  //   居中骑在边线上：右侧点在 right:-3px、左侧点在 left:-3px（点直径 6px，半径 3px）。
  const dotStyle = {
    position: 'absolute',
    top: '50%',
    [isLeft ? 'left' : 'right']: '-3px',
    transform: 'translateY(-50%)',
    width: '6px', height: '6px',
    background: '#22c55e',
    borderRadius: '50%',
    pointerEvents: 'none',
    zIndex: 12
  };

  // 3) 可见圆圈：跟随时定位到鼠标本地坐标；否则回落到边线外的默认位（用于节点 hover/选中）。
  const showCircle = !pressing && !menuOpen && (follow || visible);
  const offsetKey = isLeft ? 'right' : 'left';
  const scale = showCircle ? 1 : 0;
  // 缩放锚点朝节点侧：右侧点朝左收、左侧点朝右收 → 收拢/展开都朝节点方向，两侧同步。
  const originX = isLeft ? 'right' : 'left';
  const easePop = 'cubic-bezier(0.34, 1.56, 0.64, 1)';    // 出现：弹出（轻微过冲）
  const easeBack = 'cubic-bezier(0.36, 0, 0.66, -0.36)';  // 消失：向节点收拢 + 回弹
  const circleStyle = {
    position: 'absolute',
    boxSizing: 'border-box',
    width: '22px', height: '22px',
    background: 'var(--connect-circle-bg)',
    border: '1.5px solid var(--connect-circle-border)',
    borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'crosshair',
    opacity: showCircle ? 0.8 : 0,  // 拾取圈可见时不透明度 80%
    pointerEvents: showCircle ? 'all' : 'none',
    transformOrigin: `${originX} center`,
    zIndex: 11,
    ...(follow
      // 跟随态：定位到鼠标本地坐标（位置不加过渡以跟手），scale 播收拢/弹出
      ? { left: `${follow.x - 11}px`, top: `${follow.y - 11}px`, transform: `scale(${scale})`,
          transition: `opacity 0.18s ease, transform 0.26s ${showCircle ? easePop : easeBack}` }
      // 固定态（节点 hover/选中）：出现时向外滑出+弹出；消失时向节点收拢回弹
      : { top: '50%', [offsetKey]: showCircle ? '14px' : '4px', transform: `translateY(-50%) scale(${scale})`,
          transition: `opacity 0.18s ease, transform 0.26s ${showCircle ? easePop : easeBack}, ${offsetKey} 0.2s ease` })
  };

  return (
    <Handle
      ref={handleRef}
      type={type} position={position} id={id} isConnectable={isConnectable}
      style={handleStyle}
      onMouseMove={onZoneMove}
      onMouseLeave={onZoneLeave}
      {...props}
    >
      {/* 连接点绿点：很小的实心圆点，压在节点边线上，始终可见 */}
      <div style={dotStyle} />
      {/* 触发区：接近边线即出现圆圈并跟随鼠标；按下即从边线拉线（不必先选中/等圆圈出现）。
          className 带 source/target —— 按下时 React Flow 用 elementFromPoint 命中的是这个区，
          必须带类型类才不判空而中止连接。连线端点仍按 Handle 本体(1px,文档序在前)算，本区不影响。
          nodrag/nopan 防止在此按下拖动误触发画布平移/节点拖动。 */}
      <div className={`${type} nodrag nopan`} style={zoneStyle}
        onMouseDown={onCirclePress} onTouchStart={onCirclePress} />
      {/* 可见圆圈：拖它 = 从边线拉线（mousedown 冒泡到 Handle 触发连线）。
          className 带 source/target —— elementFromPoint 命中的是它，必须带类型类才不判空。 */}
      <div className={`${type} nodrag`} style={circleStyle}
        onMouseDown={onCirclePress} onTouchStart={onCirclePress}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--connect-circle-fg)"
          strokeWidth="2.5" strokeLinecap="round" style={{ display: 'block', pointerEvents: 'none' }}>
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </div>
    </Handle>
  );
};

  const CustomNode = memo(({ id, data, selected, xPos, yPos, isConnectable }) => {
  const updateNodeInternals = useUpdateNodeInternals();
  const { zoom } = useViewport();
  const nodeBoxRef = useRef(null);
  // 🔗 端口显隐：悬停节点时出现；节点被选中时常驻（含 iPad 触屏选中）。
  //   端口探到节点外面也算 hover（mouseleave 只在离开元素及所有后代时才触发）。
  const [nodeHovered, setNodeHovered] = useState(false);
  
  // 🎛️ 控制台开关状态
  // 📌 钉住状态持久化：优先读 data.consolePinned（用户上次的选择），
  //   新建分镜大师节点尚无该字段时回退到 data.defaultPinned（首次默认钉住）。
  const initialPinned = data.consolePinned !== undefined ? !!data.consolePinned : !!data.defaultPinned;
  const [showConsole, setShowConsole] = useState(initialPinned);
  // 📌 控制台钉住状态：钉住后不会因失去选中而收起
  const [consolePinned, setConsolePinned] = useState(initialPinned);
  // 🎛️ 是否由连线触发激活（用于保持工具条和控制台打开）
  const [activatedByConnection, setActivatedByConnection] = useState(false);
  
  // 🎯 通过 Context 获取所有回调函数（如果 Provider 存在）
  // 优先使用 Context 回调，否则回退到 data 中的回调
  const contextCallbacks = useNodeCallbacks();
  
  // 【关键修复】统一获取回调的方式，优先 context，备用 data
  const getCallback = useCallback((name) => {
    return contextCallbacks?.[name] || data[name];
  }, [contextCallbacks, data]);
  
  const [isExpanded, setIsExpanded] = useState(false); 
  // 方案 A 核心：优先使用 data.customSize 锁定尺寸。
  // 统一默认尺寸：320x240
  const [size, setSize] = useState({ 
      width: data.customSize?.width || 320, 
      height: data.customSize?.height || 240 
  });

  // 【核心修复】性能优化
  // 旧代码：const selectionCount = useStore((s) => s.getNodes().filter(n => n.selected).length);
  // 新代码：直接在 selector 内部判断是否满足“单选且选中自己”的条件
  // 这样只有当"是否单选"这个结果变化时，组件才会重渲染，而不是每次有人点击舞台都渲染
  const isSingleSelect = useStore(useCallback((store) => {
    // 这里使用 internal store 避免重新生成数组
    const selectedIds = Array.from(store.nodeInternals.values())
        .filter(n => n.selected)
        .map(n => n.id);
    return selectedIds.length === 1 && selectedIds[0] === id;
  }, [id]));

  // 🔌 P4 优化：一次遍历 edges 检查两个 handle 的连接状态（替代 SmartHandle 内部各自 O(n) 扫描）
  const [isTargetConnected, isSourceConnected] = useStore(
    useCallback((store) => {
      let target = false, source = false;
      for (const edge of store.edges) {
        if (!target && edge.target === id) target = true;
        if (!source && edge.source === id) source = true;
        if (target && source) break;
      }
      return [target, source];
    }, [id]),
    (a, b) => a[0] === b[0] && a[1] === b[1]
  );

  const outputs = data.outputs || (data.output ? [data.output] : []);
  const isUploadRetrying = !data.loading && !data.error && data.statusInfo?.phase === 'retrying_upload';
  const isVideoNode = data.type === 'video';
  const isImageNode = data.type === 'image';
  const isAudioNode = data.type === 'audio';
  const isTextNode = data.type === 'text';

  // 多图层节点检测：仅用于样式提示，不再隐藏输出端口
  // 连接限制由 onConnect 验证逻辑处理（PSD 节点禁止连接到图像/视频模型）
  const hasImageOverlays = false;

  // 🎯 统一的"节点激活"状态：用于控制工具条和控制台的显示
  // 当节点被单选，或者由连线触发激活时，都算"激活"
  const isNodeActive = isSingleSelect || activatedByConnection;

  // 🎯 hasConsole：当节点激活且不是文本/直传/系统咒语节点时，才有控制台区域
  // 📌 钉住且控制台打开时，即使未选中也显示
  const isSystemSpell = !!data.isSystemSpell;
  const hasConsole = (isNodeActive || (consolePinned && showConsole)) && !data.isDirectUpload && !isTextNode && !isSystemSpell;

  // 监听 data.customSize 变化，并同步更新 local state (size)。
  // 确保了如果外部（如 Resize 操作后）更新了 customSize，组件能重新渲染为新尺寸。
  useEffect(() => { 
      if (data.customSize) {
          setSize(data.customSize); 
          updateNodeInternals(id); 
      }
  }, [data.customSize, id, updateNodeInternals]);

  // 🆕 描述词编辑弹窗开关（资产节点左下角 T 角标）
  const [showDescEdit, setShowDescEdit] = useState(false);

  // 比例预览动画标志：_ratioPreview 为 true 时启用 CSS transition，动画结束后自动清除
  const [animating, setAnimating] = useState(false);
  useEffect(() => {
    if (data._ratioPreview) {
      setAnimating(true);
      const timer = setTimeout(() => {
        setAnimating(false);
        // 清除标志，避免影响后续操作
        const onUpdate = getCallback('onUpdate');
        onUpdate?.(id, { _ratioPreview: undefined });
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [data._ratioPreview, id, getCallback]);

  // 如果失去选中（包括进入多选模式），自动收起堆叠卡片
  useEffect(() => { if (!isSingleSelect) setIsExpanded(false); }, [isSingleSelect]);

  // 🎛️ 失去选中时重置控制台状态（重要：确保下次选中时默认关闭）
  // 📌 钉住时不收起
  useEffect(() => {
    if (!isSingleSelect && !activatedByConnection && !consolePinned) {
      setShowConsole(false);
    }
  }, [isSingleSelect, activatedByConnection, consolePinned]);

  // 🎛️ 监听连线触发：只要检测到 openConsoleOnConnect 标记就立即打开控制台和工具条
  useEffect(() => {
    if (data.openConsoleOnConnect) {
      setShowConsole(true);
      setActivatedByConnection(true);  // 标记为连线触发激活
      // 清除标记，避免重复触发
      const onUpdate = getCallback('onUpdate');
      onUpdate?.(id, { openConsoleOnConnect: undefined });
    }
  }, [data.openConsoleOnConnect, id, getCallback]);

  // 🎛️ 当节点被正常选中时（点击选中），清除连线激活标记
  useEffect(() => {
    if (isSingleSelect && activatedByConnection) {
      // 节点被正常选中了，不再需要连线激活保护
      setActivatedByConnection(false);
    }
  }, [isSingleSelect, activatedByConnection]);

  // 🎛️ 监听全局点击：当连线激活状态下，用户点击其他地方，清除激活状态
  // 📌 钉住时不收起
  useEffect(() => {
    if (!activatedByConnection) return;
    
    const handleGlobalClick = (e) => {
      // 检查点击是否在当前节点内
      if (nodeBoxRef.current && !nodeBoxRef.current.contains(e.target)) {
        // 点击在节点外部，清除连线激活状态
        setActivatedByConnection(false);
        if (!consolePinned) {
          setShowConsole(false);
        }
      }
    };
    
    // 延迟添加监听器，避免捕获连线完成时的点击
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleGlobalClick);
    }, 100);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleGlobalClick);
    };
  }, [activatedByConnection, consolePinned]);

  // 【修复】统一使用 getCallback 获取回调，确保兼容性
  const handleUpdate = useCallback((newData) => {
    const onUpdate = getCallback('onUpdate');
    onUpdate?.(id, newData);
  }, [id, getCallback]);

  // 🎛️ 控制台开关回调
  // 📌 手动折叠时自动取消钉住状态（并持久化）
  const handleToggleConsole = useCallback(() => {
    setShowConsole(prev => {
      if (prev) {
        // 正在收起 → 取消钉住
        setConsolePinned(false);
        handleUpdate({ consolePinned: false });
      }
      return !prev;
    });
  }, [handleUpdate]);

  // 📌 分镜大师节点不再强制置顶：与其它图层一视同仁，
  //   仅依赖 React Flow 的 elevateNodesOnSelect —— 哪个节点被选中，哪个就在最上层。

  // 📌 钉住/取消钉住回调
  // 钉住状态持久化到 data.consolePinned：手动取消后刷新仍保持取消
  const handleTogglePin = useCallback(() => {
    setConsolePinned(prev => {
      const next = !prev;
      handleUpdate({ consolePinned: next });
      return next;
    });
  }, [handleUpdate]);

  // 🛡️ 存储事件处理函数引用，便于清理
  const resizeHandlersRef = useRef({ onMouseMove: null, onMouseUp: null });
  
  // 🛡️ 组件卸载时清理可能遗留的事件监听器
  useEffect(() => {
    return () => {
      if (resizeHandlersRef.current.onMouseMove) {
        document.removeEventListener('mousemove', resizeHandlersRef.current.onMouseMove);
      }
      if (resizeHandlersRef.current.onMouseUp) {
        document.removeEventListener('mouseup', resizeHandlersRef.current.onMouseUp);
      }
    };
  }, []);

  const onResizeMouseDown = (e) => {
    e.stopPropagation(); e.preventDefault();
    const startX = e.clientX, startY = e.clientY, startW = size.width, startH = size.height;
    const ratio = startW / startH;
    let finalSize = { width: startW, height: startH };
    const onMouseMove = (m) => {
      // 图像/视频有顶部信息条（分辨率+文件大小），最小宽度提到 MIN_NODE_WIDTH 防止挤压换行；
      // 文本/音频无信息条，保持 80 的下限
      const minW = (isAudioNode || isTextNode) ? 80 : MIN_NODE_WIDTH;
      const fw = Math.max(minW, startW + (m.clientX - startX));
      const fh = (isAudioNode || isTextNode) ? Math.max(60, startH + (m.clientY - startY)) : fw / ratio; 
      finalSize = { width: fw, height: fh };
      if (nodeBoxRef.current) { nodeBoxRef.current.parentElement.style.width = `${fw}px`; nodeBoxRef.current.parentElement.style.height = `${fh}px`; }
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp);
      resizeHandlersRef.current = { onMouseMove: null, onMouseUp: null };
      setSize(finalSize); handleUpdate({ customSize: finalSize }); updateNodeInternals(id);
    };
    // 🛡️ 保存引用以便清理
    resizeHandlersRef.current = { onMouseMove, onMouseUp };
    document.addEventListener('mousemove', onMouseMove); document.addEventListener('mouseup', onMouseUp);
  };

  const handleSetMain = (idx) => {
    const newOutputs = [...outputs];
    const oldMain = newOutputs[0];
    const selectedOutput = newOutputs[idx];
    newOutputs[0] = selectedOutput; // 选中的副图 → 主图位置
    newOutputs[idx] = oldMain; // 旧主图 → 副图位置
    handleUpdate({
      outputs: newOutputs,
      output: newOutputs[0],
      localCacheUrl: null,  // 清除缓存URL，强制刷新
      _preserveSize: true,  // 防止 ImageNodeView.onLoad 重算节点卡片尺寸
    });
  };
  
  const handleRemoveStack = useCallback((url) => {
    const newOutputs = outputs.filter(u => u !== url);
    handleUpdate({ outputs: newOutputs, output: newOutputs[0] || null });
  }, [outputs, handleUpdate]);

  // 【修复】使用 getCallback 获取 onDetach
  const handleDetachStack = useCallback((url) => {
    const onDetach = getCallback('onDetach');
    onDetach?.(id, url, { x: xPos, y: yPos }); 
  }, [id, xPos, yPos, getCallback]);

  // 新增：标题编辑状态
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const inputRef = useRef(null);
  
  // 使用 Hook 全局保护输入状态
  useInputProtection(inputRef, isEditingTitle);

  // 本地编辑缓冲，避免每次按键触发父级状态更新从而导致光标跳到末尾
  const [localTitle, setLocalTitle] = useState(data.title || '');
  // 当外部 data.title 变化且不在编辑状态时同步本地缓冲 + DOM
  useEffect(() => {
    if (!isEditingTitle) {
      setLocalTitle(data.title || '');
      if (inputRef.current) inputRef.current.value = data.title || '';
    }
  }, [data.title, isEditingTitle]);

  // MJ 工具条：仅在「选中节点 + MJ 生成完成」时显示（类似底部控制台的 isNodeActive 逻辑）
  // 未选中时隐藏，避免画布上工具条过多造成视觉干扰
  // 支持所有 MJ 渠道：yunwu-midjourney, apimart-midjourney
  const isMjVendor = data.vendor === 'yunwu-midjourney' || data.vendor === 'apimart-midjourney';
  const showMjToolbar = isNodeActive && isMjVendor && data.vendorTaskId && !data.loading && data.mjPhase;
  const handleMjAction = getCallback('onMjAction');
  const getMjToolboxConfig = getCallback('getMjToolboxConfig');
  // 根据当前节点的 vendor 获取对应的工具箱配置
  const mjToolboxConfig = getMjToolboxConfig?.(data.vendor) || {};

  // GPT 局部重绘工具条：选中的非 MJ 图片节点且有图片时显示（排除分镜草图节点）
  const getGptInpaintConfig = getCallback('getGptInpaintConfig');
  const gptInpaintConfig = getGptInpaintConfig?.() || {};
  const showGptInpaintToolbar = isNodeActive && isImageNode && !data.loading
    && (data.output || data.localCacheUrl)
    && !isMjVendor  // 排除所有 MJ 渠道
    && !data.storyboardMode
    && gptInpaintConfig.enabled;
  const handleGptInpaintAction = getCallback('onGptInpaint');

  // H3 升 2K 工具条：选中的 H3 视频节点、已出片、且当前不是 2K 时显示
  // （2K 已是上限，再升没有意义；上游也只接受 768P 源）
  const getH3UpscaleConfig = getCallback('getH3UpscaleConfig');
  const h3UpscaleConfig = getH3UpscaleConfig?.() || {};
  const showH3UpscaleToolbar = isNodeActive && isVideoNode && !data.loading
    && (data.output || data.localCacheUrl)
    && data.vendor === 'apimart-minimax-h3'
    && data.vendorTaskId
    && String(data.params?.resolution || '') !== '2K'
    && h3UpscaleConfig.enabled;
  const handleH3UpscaleAction = getCallback('onH3Upscale');

  return (
    <div className="custom-node-wrapper" onMouseEnter={() => setNodeHovered(true)} onMouseLeave={() => setNodeHovered(false)} style={{ position: 'relative', width: `${size.width}px`, height: `${size.height}px`, transition: animating ? 'width 0.3s ease, height 0.3s ease' : undefined }}>
      {/* MJ 工具条 */}
      {showMjToolbar && (
        <MjToolbar
          phase={data.mjPhase}
          nodeId={id}
          onAction={handleMjAction}
          nodeWidth={size.width}
          zoom={zoom}
          toolboxName={mjToolboxConfig.name}
          toolboxLogo={mjToolboxConfig.logo}
          actionLabels={mjToolboxConfig.actionLabels || {}}
          disabledActions={mjToolboxConfig.disabledActions || []}
          creditCost={mjToolboxConfig.creditCost || 0}
          mjButtons={data.mjButtons}
          mjActionType={data.mjActionType}
          vendor={data.vendor}
        />
      )}
      {/* GPT 局部重绘工具条 */}
      {showGptInpaintToolbar && (
        <GptInpaintToolbar
          nodeId={id}
          onAction={(nodeId) => handleGptInpaintAction?.(nodeId)}
          zoom={zoom}
          toolboxName={gptInpaintConfig.name}
          toolboxLogo={gptInpaintConfig.logo}
          creditCost={gptInpaintConfig.creditCost || 0}
        />
      )}
      {/* H3 升 2K 工具条 */}
      {showH3UpscaleToolbar && (
        <H3UpscaleToolbar
          nodeId={id}
          onAction={(nid) => handleH3UpscaleAction?.(nid)}
          zoom={zoom}
          toolboxName={h3UpscaleConfig.name}
          toolboxLogo={h3UpscaleConfig.logo}
          creditCost={h3UpscaleConfig.creditCost || 0}
        />
      )}
      {/* 顶部标题栏 */}
      <div style={styles.titleWrapper(selected ? 'var(--node-title-selected)' : 'var(--node-title)', zoom, size.width)}>
        <div style={styles.iconBadge(data.type)}>
          {isVideoNode ? <IconFilm size={16} /> : isImageNode ? <IconPhoto size={16} /> : isTextNode ? <Sparkles size={16} strokeWidth={1.5} /> : <IconMusic size={16} />}
        </div>
        <input
          className="nodrag node-title-input"
          ref={inputRef}
          defaultValue={data.title || ''}
          readOnly={isSystemSpell}
          onChange={(e) => {
            if (isSystemSpell) return;
            // 编辑模式：仅更新本地缓冲，延迟同步到父级
            if (isEditingTitle) {
              setLocalTitle(e.target.value);
              return;
            }
            // 非编辑模式（程序性修改）立即同步
            handleUpdate({ title: e.target.value });
          }}
          style={styles.nodeTitleInput}
          onMouseDown={e => { e.stopPropagation(); setIsEditingTitle(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // 提交并退出编辑
              const val = inputRef.current ? inputRef.current.value : localTitle;
              handleUpdate({ title: val });
              e.currentTarget.blur();
              setIsEditingTitle(false);
            } else if (e.key === 'Escape') {
              // 放弃编辑，恢复为服务端值
              if (inputRef.current) inputRef.current.value = data.title || '';
              setLocalTitle(data.title || '');
              e.currentTarget.blur();
              setIsEditingTitle(false);
            }
          }}
          onBlur={() => {
            // 失焦时提交本地缓冲并退出编辑
            if (isEditingTitle) {
              const val = inputRef.current ? inputRef.current.value : localTitle;
              handleUpdate({ title: val });
            }
            setIsEditingTitle(false);
          }}
        />
      </div>

      {/* 生成图片节点：右上角剥离按钮（与关闭按钮同风格，避免重叠） */}
      {isNodeActive && !isSystemSpell && isImageNode && !data.isDirectUpload && !data.assetId && !data.isDetached && (data.output || data.localCacheUrl) && (
        <div
          className="nodrag"
          onClick={(e) => {
            e.stopPropagation();
            getCallback('onDetach')?.(id, data.localCacheUrl || data.output, { x: xPos, y: yPos }, 'image');
          }}
          title="剥离到独立节点"
          style={{ ...styles.deleteBtn, right: outputs.length > 1 ? '54px' : '30px' }}
        >
          <ExternalLink size={11} color="rgba(255,255,255,0.85)" />
        </div>
      )}

      {/* 删除按钮（系统咒语不显示删除） */}
      {isNodeActive && !isSystemSpell && <div className="nodrag" onClick={() => getCallback('onDelete')?.(id)} style={styles.deleteBtn}><X size={12} color="rgba(255,255,255,0.8)" /></div>}

      {/* 🆕 描述词按钮（左下角）——仅从资料库带过来的图片/视频资产节点
          为什么只给资产节点：这类节点 data.isDirectUpload 为 true，被
          hasConsole 排除在外（第 281 行），根本没有底部控制台，描述词
          在画布上无处可改。生成节点有控制台，不需要这个入口。
          样式对齐多图层模式那颗圆按钮（ImageNodeView.js:1431）：24px 圆形、
          blur、hover 放大 1.1。改用琥珀色 + 文档图标与蓝色的图层按钮区分开。
          有描述词时实心不透明、没有时半透明，一眼能看出哪个节点带描述词。
          ⚠️ 视频节点要抬到 36px：VideoNodeView 底部控制栏是 bottom:0 通栏，
          实测高约 29px（4px padding + 14px 播放按钮 + 3px gap + 4px 进度条
          + 4px padding），贴 8px 会正好压住播放按钮。36px 留 7px 余量。 */}
      {isNodeActive && !isSystemSpell && (data.assetId || id.startsWith('mention-')) && (isImageNode || isVideoNode) && (
        <div
          className="nodrag"
          onClick={(e) => { e.stopPropagation(); setShowDescEdit(true); }}
          title={data.description ? `描述词：${data.description}` : '添加描述词'}
          style={{
            ...descBadgeStyle(24, !!data.description),
            position: 'absolute',
            left: 8, bottom: isVideoNode ? 36 : 8, zIndex: 1001,
          }}
          onMouseEnter={descBadgeHover.onMouseEnter(!!data.description)}
          onMouseLeave={descBadgeHover.onMouseLeave(!!data.description)}
        >
          <DescriptionIcon size={17} />
        </div>
      )}

      {/* 🆕 描述词编辑弹窗：改 data.description 即可，下游已连好的参考条目
          由 useNodeActions 的 needsRefSync 分支自动跟着更新（第 86/143 行） */}
      {showDescEdit && (
        <DescriptionEditModal
          item={{ title: data.title, description: data.description }}
          onSave={(newDesc) => handleUpdate({ description: newDesc })}
          onCancel={() => setShowDescEdit(false)}
        />
      )}

      {outputs.length > 1 && <div style={styles.stackFrame} />}

      <div ref={nodeBoxRef} style={{ 
          width: '100%', height: '100%', borderRadius: isTextNode ? '8px' : '0px',
          border: `0.5px solid ${selected ? 'var(--node-border-selected)' : 'var(--node-border)'}`,
          background: isImageNode ? 'var(--node-bg-image)' : (isVideoNode ? 'var(--node-bg-video)' : 'var(--node-bg)'),
          display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
          transition: 'margin-bottom 0.1s ease', 
          marginBottom: hasConsole ? `${44 / zoom}px` : '0px' 
      }}>
        {isUploadRetrying && outputs.length > 0 && (
          <div style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 120,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 8px',
            borderRadius: 999,
            border: '1px solid rgba(251,191,36,0.55)',
            background: 'rgba(120,53,15,0.72)',
            color: 'rgba(254,240,138,0.95)',
            fontSize: 11,
            lineHeight: 1,
            pointerEvents: 'none',
          }}>
            <Loader2 size={11} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
            转存重试中
          </div>
        )}

        {/* Idle State Background (Static Nebula) */}
        {!isTextNode && !outputs.length && !data.loading && !data.error && (
            <NebulaBackground type={data.type} animated={false} opacity={0.95} />
        )}

        {outputs.length > 1 && (
          <div className="nodrag" style={styles.stackBadge} onClick={() => setIsExpanded(!isExpanded)}>
            <span style={{ fontWeight: 'bold' }}>{outputs.length}</span>
            <ChevronRight size={10} style={{ transform: isExpanded ? 'rotate(90deg)' : 'none', transition: '0.3s' }} />
          </div>
        )}

        {/* ── Loading 遮罩（两种模式）── */}
        {data.loading && data.clipping ? (
          /* ✂️ 裁剪模式：封面缩略图 + 半透明 Loader2 旋转（与智能抠图风格一致） */
          <>
            {data.clippingThumbnail && (
              <img src={data.clippingThumbnail} alt="" style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                objectFit: 'cover', zIndex: 99,
              }} />
            )}
            <div style={{
              position: 'absolute', inset: 0, zIndex: 100,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.35)',
              borderRadius: 'inherit',
              pointerEvents: 'none',
            }}>
              <Loader2 size={18} strokeWidth={2} color="rgba(255,255,255,0.6)" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ marginTop: 4, fontSize: 6, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.3px', fontWeight: 500 }}>
                {data.statusInfo?.status || '裁剪中...'}
              </span>
            </div>
          </>
        ) : data.loading ? (
          /* 🎬 AI 生成模式：完整 LoadingOverlay（星云背景 + 计时器）
             分镜节点重新生成（已有上一轮成品图）时走 transparent：旧图不消失、整图变暗，
             上面叠 spinner + 模型 + 进度 + 计时，与普通图片节点生成观感一致。 */
          <LoadingOverlay
            type={data.type}
            statusInfo={data.statusInfo}
            generationStartTime={data.generationStartTime}
            modelName={data.generationModelName || data.modelName || "测试模型"}
            modelLogo={data.generationModelLogo || data.modelLogo}
            transparent={data.storyboardMode && !!data.output}
          />
        ) : null}

        {isUploadRetrying && (
          <div style={{
            position: 'absolute',
            inset: 0,
            zIndex: 96,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(12, 18, 28, 0.72)',
            borderRadius: '13px',
            padding: '12px',
            boxSizing: 'border-box',
          }}>
            <Loader2 size={18} strokeWidth={2} color="rgba(255,255,255,0.75)" style={{ animation: 'spin 1s linear infinite' }} />
            <div style={{
              marginTop: '8px',
              color: 'rgba(255,255,255,0.88)',
              fontSize: '12px',
              textAlign: 'center',
              lineHeight: 1.45,
              wordBreak: 'break-word',
            }}>
              {data.statusInfo?.status || '文件转存重试中'}
            </div>
            <div style={{
              marginTop: '4px',
              color: 'rgba(255,255,255,0.65)',
              fontSize: '11px',
              textAlign: 'center',
            }}>
              若最终失败将自动退款
            </div>
          </div>
        )}
        
        {data.error ? (
            <div style={{ 
              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
              background: 'rgba(30, 30, 30, 0.95)', borderRadius: '13px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: '20px', boxSizing: 'border-box', zIndex: 100
            }}>
              <AlertTriangle size={36} color="#fbbf24" style={{ marginBottom: '16px', opacity: 0.9 }} />
              
              <div style={{ 
                color: 'rgba(255,255,255,0.8)', fontSize: '13px', textAlign: 'center',
                maxHeight: '60%', width: '100%', overflow: 'hidden', lineHeight: '1.5',
                wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                padding: '0 8px', marginBottom: '20px'
              }}>
                {typeof data.error === 'object' ? JSON.stringify(data.error, null, 2) : data.error}
              </div>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                {/* 重试按钮 */}
                <button 
                  className="nodrag"
                  onClick={() => getCallback('onGenerate')?.(id)}
                  title="重试"
                  style={{ 
                    width: '32px', height: '32px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.9)', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
                >
                  <RotateCcw size={16} />
                </button>
                {/* 取消按钮：清除错误状态，恢复为未生成状态 */}
                <button 
                  className="nodrag"
                  onClick={() => getCallback('onUpdate')?.(id, { error: null, loading: false, output: null, outputs: null, generationTime: null, generationStartTime: null, statusInfo: null })}
                  title="取消"
                  style={{ 
                    width: '32px', height: '32px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.9)', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,100,100,0.2)'; e.currentTarget.style.borderColor = 'rgba(255,100,100,0.5)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
         ) : (
          isTextNode ? <TextNodeView id={id} data={data} selected={isSingleSelect} handleUpdate={handleUpdate} zoom={zoom} isSystemSpell={isSystemSpell} /> : 
          isVideoNode ? <VideoNodeView id={id} data={data} selected={isNodeActive || (consolePinned && showConsole)} isSingleSelect={isSingleSelect} xPos={xPos} yPos={yPos} zoom={zoom} showConsole={showConsole} onToggleConsole={handleToggleConsole} /> :
          isImageNode ? <ImageNodeView id={id} data={data} selected={(isNodeActive || (consolePinned && showConsole)) && !(data.storyboardMode && consolePinned && showConsole)} isSingleSelect={isSingleSelect} xPos={xPos} yPos={yPos} handleUpdate={handleUpdate} zoom={zoom} showConsole={showConsole} onToggleConsole={handleToggleConsole} /> :
          isAudioNode && outputs[0] ? <AudioNodeView id={id} data={data} selected={isSingleSelect} xPos={xPos} yPos={yPos} zoom={zoom} /> : 
          <ImageIconLucide size={48} color="rgba(255,255,255,0.1)" />
        )}
        <div className="nodrag resize-handle-trigger" onMouseDown={onResizeMouseDown} style={{ ...styles.resizeHandle, display: (consolePinned && showConsole) ? 'none' : undefined }} />
      </div>

      {isExpanded && outputs.slice(1).map((url, idx) => (
        <ExtraCard key={idx} idx={idx} total={outputs.length} url={url} size={size} onSetMain={handleSetMain} onDetach={handleDetachStack} onRemove={handleRemoveStack} isVideo={isVideoNode} />
      ))}

      {/* ━━━ 连接点（Handle）渲染 ━━━ */}
      {/* 📍 左侧输入点：所有非文本节点都有（文本节点只有输出） */}
      {!isTextNode && (
        <SmartHandle type="target" position={Position.Left} isConnectable={isConnectable} isConnected={isTargetConnected} zoom={zoom} visible={nodeHovered || selected} />
      )}

      {/* 📍 右侧输出点：所有节点都有（PSD 节点在有覆盖物时隐藏） */}
      <SmartHandle 
        type="source" 
        position={Position.Right} 
        isConnectable={isConnectable} 
        isConnected={isSourceConnected}
        zoom={zoom} 
        visible={nodeHovered || selected}
        style={hasImageOverlays ? { display: 'none' } : {}} 
      />

      {/* 底部控制台 - 始终挂载，用 display 控制显示/隐藏，避免状态丢失 */}
      {hasConsole && (
        <div style={{ display: showConsole ? 'block' : 'none' }}>
          <NodeConsole key={id} data={data} id={id} zoom={zoom} handleUpdate={handleUpdate} consolePinned={consolePinned} onTogglePin={handleTogglePin} />
        </div>
      )}
    </div>
  );
});

export default CustomNode;